/**
*
* @param {*} events:
* The following function should be in the “api.js” file.
* This function takes an events array, then uses map to create a new array with only locations.
* It will also remove all duplicates by creating another new array using the spread operator and spreading a Set.
* The Set will remove all duplicates from the array.
*/

import mockData from './mock-data';
import NProgress from "nprogress";

/**
 * Extracts unique event locations from an array of events.
 * @param {Array} events - An array of event objects.
 * @returns {Array} An array of unique event locations.
 */
export const extractLocations = (events) => {
    const extractedLocations = events.map((event) => event.location);
    const locations = [...new Set(extractedLocations)];
    return locations;
};
  
/**
 * Checks the validity of an access token by making an HTTP request.
 * @param {string} accessToken - The access token to be checked.
 * @returns {Promise<Object>} A promise that resolves to the token information.
 */
const checkToken = async (accessToken) => {
    const response = await fetch(
      `https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${accessToken}`
    );
    const result = await response.json();
    return result;
};

/**
 * Removes query parameters from the current URL and updates the history state.
 */
const removeQuery = () => {
    let newurl;
    if (window.history.pushState && window.location.pathname) {
      newurl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname;
      window.history.pushState("", "", newurl);
    } else {
      newurl = window.location.protocol + "//" + window.location.host;
      window.history.pushState("", "", newurl);
    }
};

/**
 * Retrieves events data from the API.
 * @returns {Promise<Array>} A promise that resolves to an array of event objects.
 */
export const getEvents = async () => {

    if (window.location.href.startsWith("http://localhost")) {
      return mockData;
    }
  
    if (!navigator.onLine) {
        const events = localStorage.getItem("lastEvents");
        NProgress.done();
        return events?JSON.parse(events):[];
    }

    const token = await getAccessToken();
  
    if (token) {
      removeQuery();
      const url =  "https://hayq3gp460.execute-api.us-east-1.amazonaws.com/dev/api/get-events" + "/" + token;
      const response = await fetch(url);
      const result = await response.json();
      if (result) {
        NProgress.done();
        localStorage.setItem("lastEvents", JSON.stringify(result.events));
        return result.events;
      } else return null;
    }
};

/**
 * Retrieves the access token from local storage or initiates the authorization process.
 * @returns {Promise<string>} A promise that resolves to the access token.
 */
export const getAccessToken = async () => {
    const accessToken = localStorage.getItem('access_token');
    const tokenCheck = accessToken && (await checkToken(accessToken));

    if (!accessToken || tokenCheck.error) {
      await localStorage.removeItem("access_token");
      const searchParams = new URLSearchParams(window.location.search);
      const code = await searchParams.get("code");
      if (!code) {
        const response = await fetch(
          "https://hayq3gp460.execute-api.us-east-1.amazonaws.com/dev/api/get-auth-url"
        );
        const result = await response.json();
        const { authUrl } = result;
        return (window.location.href = authUrl);
      }
      return code && getToken(code);
    }
    return accessToken;
};

/**
 * Exchanges the authorization code for an access token.
 * @param {string} code - The authorization code to be exchanged.
 * @returns {Promise<string>} A promise that resolves to the access token.
 */
const getToken = async (code) => {
    const encodeCode = encodeURIComponent(code);
    const response = await fetch(
      'https://hayq3gp460.execute-api.us-east-1.amazonaws.com/dev/api/token' + '/' + encodeCode
    );
    const { access_token } = await response.json();
    access_token && localStorage.setItem("access_token", access_token);
  
    return access_token;
};

